import clsx from 'clsx';
import { Info, RefreshCw, Wallet } from 'lucide-react';
import { formatBudgetHint } from '@/utils/adPlatformMinBudgets';

export type BillingPlatform = 'meta' | 'tiktok' | 'google';

const NAME: Record<BillingPlatform, string> = { meta: 'Facebook', tiktok: 'TikTok', google: 'Google Ads' };

/**
 * "Billed in PKR" — shown in each ad module's header so nobody types an AED
 * budget into a PKR account. Unknown currency shows a refresh action instead
 * of guessing one.
 */
export function BilledInBadge({ platform, currency, onRefresh, refreshing, className }: {
  platform: BillingPlatform;
  currency: string | null | undefined;
  onRefresh?: () => void;
  refreshing?: boolean;
  className?: string;
}) {
  if (!currency) {
    return (
      <span className={clsx('inline-flex h-8 items-center gap-1.5 rounded-full border border-warning/40 bg-warning-soft px-3 text-xs font-medium text-warning', className)}
        title={`We don't know which currency this ${NAME[platform]} account bills in yet.`}>
        <Wallet size={13} /> Currency unknown
        {onRefresh && (
          <button type="button" onClick={onRefresh} disabled={refreshing} className="ml-0.5 inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline disabled:opacity-60">
            <RefreshCw size={12} className={clsx(refreshing && 'animate-spin')} /> Refresh
          </button>
        )}
      </span>
    );
  }
  return (
    <span className={clsx('inline-flex h-8 items-center gap-1.5 rounded-full border border-border bg-surface px-3 text-xs font-medium text-text-secondary', className)}
      title={`This ${NAME[platform]} account bills in ${currency}. ${NAME[platform]} sets this — enter budgets and bids in ${currency}.`}>
      <Wallet size={13} className="text-primary" /> Billed in <b className="font-semibold text-text-primary">{currency}</b>
    </span>
  );
}

/** Info icon next to a budget or bid label, with the billing-currency explanation on hover. */
export function CurrencyTip({ platform, currency }: { platform: BillingPlatform; currency: string | null | undefined }) {
  return (
    <span className="inline-flex cursor-help align-middle text-text-muted" title={formatBudgetHint(platform, currency)} aria-label={formatBudgetHint(platform, currency)}>
      <Info size={13} />
    </span>
  );
}
